import {
  collection, doc, getDoc, getDocs, query, runTransaction, serverTimestamp, where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { WHT_MODELS } from "./whtCalculator";
import { GST_SCOPES } from "./gstCalculator";

const COLLECTION = "client_tax_profiles";
// One doc per client, always mirroring whichever client_tax_profiles version
// is currently ACTIVE. Exists because the Firestore Web SDK's
// transaction.get() only accepts a document reference, not a query (that's
// only supported by the server-side Admin SDK) — every approval transaction
// needs to look up a client's active profile mid-transaction, so that lookup
// has to be a single doc read, not a "clientCode + status==ACTIVE" query.
const ACTIVE_POINTERS = "client_tax_profile_active";

export const CLIENT_CODES = Object.freeze({
  CORTEVA: "CORTEVA",
  IIL: "IIL",
  CBRE: "CBRE",
  ARSL: "ARSL",
  ATL: "ATL",
  ATL_COCACOLA: "ATL_COCACOLA",
  ES: "ES",
  CPBM: "CPBM",
  // The rest of ManagementFinance.js's MANAGEMENT_CLIENTS list (excluding
  // "ALM" — that's ALM itself, not a billed client; it stays as a manual
  // direct-expense entry in Management Finance, not a tax profile). These
  // six have no invoice-generation code path anywhere in the app yet (they
  // only exist as free-text options in the manual ledger) — added here
  // anyway per request, so a tax profile can exist ahead of any invoice flow
  // being built.
  PTC_AKF: "PTC_AKF",
  // AESL is Allied Engineering — the same client the General Invoice Engine's
  // headcount adapter calls "AE" (that raw code comes from the legacy backend
  // builder and is left alone; deriveGeneralClientCode() in
  // generalInvoiceRegistry.js aliases "AE" -> "AESL" so the finance layer only
  // ever sees one client code for this company).
  AESL: "AESL",
  SEFAM: "SEFAM",
  // G-Solar is actually two separate legal entities (separate NTNs, separate
  // invoices) billed off one shared payroll workbook — split into two client
  // codes so receivables/profitability/EOBI control track them independently.
  G_SOLAR: "G_SOLAR",
  VS_ALPHA: "VS_ALPHA",
  NAGINA: "NAGINA",
  UNITED_DETERGENT: "UNITED_DETERGENT",
  // Separate legal entity from CORTEVA (Corteva Agriscience Pakistan
  // Limited), sharing the same office — carries its own NTN. Only the IFS FO
  // segment of the recurring workbook, plus its own flat-fee Consultant
  // invoice, bill under this client code (see cortevaInvoiceRegistry.js).
  CORTEVA_CROP: "CORTEVA_CROP"
});

// Initial mapping per the plan (Section 4 / 7 of the approved plan) — reference
// data only, used by ClientTaxProfileAdmin.js to pre-fill a new DRAFT profile.
// Nothing here is written to Firestore automatically; every profile still
// requires an explicit admin/finance-manager create + activate action.
export const SUGGESTED_DEFAULTS = Object.freeze({
  [CLIENT_CODES.CORTEVA]: {
    clientName: "Corteva Agriscience Pakistan Limited",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07,
    gstRate: 0.16,
    gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY,
    gstWithholdingRate: 0.20,
    recoveryMethod: "NONE",
    recoveryRate: null
  },
  [CLIENT_CODES.IIL]: {
    clientName: "IIL",
    whtModel: WHT_MODELS.GROSS_INVOICE_WITH_RECOVERY,
    whtRate: 0.07,
    gstRate: 0.16,
    gstScope: GST_SCOPES.FULL_INVOICE,
    gstWithholdingRate: 0.20,
    recoveryMethod: "GROSS_UP_ADD_BACK",
    recoveryRate: 0.088376
  },
  [CLIENT_CODES.CBRE]: {
    clientName: "CBRE Corporate Outsourcing (Private) Limited",
    whtModel: WHT_MODELS.GROSS_INVOICE_WITH_RECOVERY,
    whtRate: 0,
    gstRate: 0.16,
    gstScope: GST_SCOPES.FULL_INVOICE,
    gstWithholdingRate: 0,
    recoveryMethod: "NONE",
    recoveryRate: null
  },
  [CLIENT_CODES.CPBM]: {
    clientName: "Century Board and Paper Mill",
    whtModel: WHT_MODELS.GROSS_INVOICE_WITH_RECOVERY,
    whtRate: 0,
    gstRate: 0.16,
    gstScope: GST_SCOPES.FULL_INVOICE,
    gstWithholdingRate: 0,
    recoveryMethod: "NONE",
    recoveryRate: null
  },
  [CLIENT_CODES.ARSL]: {
    clientName: "Allied Rental Services Limited",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07,
    gstRate: 0.16,
    gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY,
    gstWithholdingRate: 0.20,
    recoveryMethod: "NONE",
    recoveryRate: null
  },
  [CLIENT_CODES.ATL]: {
    clientName: "Allied Transport Limited",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07,
    gstRate: 0.16,
    gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY,
    gstWithholdingRate: 0.20,
    recoveryMethod: "NONE",
    recoveryRate: null
  },
  // Same tax treatment as ATL (the invoices are issued under ATL's name and NTN).
  [CLIENT_CODES.ATL_COCACOLA]: {
    clientName: "ATL CocaCola",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07,
    gstRate: 0.16,
    gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY,
    gstWithholdingRate: 0.20,
    recoveryMethod: "NONE",
    recoveryRate: null
  },
  [CLIENT_CODES.ES]: {
    clientName: "E-Commerce",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07,
    gstRate: 0.16,
    gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY,
    gstWithholdingRate: 0.20,
    recoveryMethod: "NONE",
    recoveryRate: null
  },
  // No documented tax treatment exists yet for these six — defaulted to
  // Model 1 (the spec's own fallback: "every other current client not
  // explicitly assigned to Model 2"). Ships DRAFT like everything else;
  // review the rate before activating any of these.
  [CLIENT_CODES.PTC_AKF]: {
    clientName: "PTC AKF",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07, gstRate: 0.16, gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY, gstWithholdingRate: 0.20,
    recoveryMethod: "NONE", recoveryRate: null
  },
  [CLIENT_CODES.AESL]: {
    clientName: "Allied Engineering",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07, gstRate: 0.16, gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY, gstWithholdingRate: 0.20,
    recoveryMethod: "NONE", recoveryRate: null
  },
  [CLIENT_CODES.SEFAM]: {
    clientName: "Sefam (Private) Limited",
    // Real invoices (Kayseria, Sep 2026) show 0% WHT and no GST withheld —
    // client pays the Sales Tax invoice (service charge + 16% GST) in full.
    // The Reimbursement invoice itself carries no tax at all (net-of-payroll-
    // deductions billing, handled entirely outside the WHT/GST model).
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0, gstRate: 0.16, gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY, gstWithholdingRate: 0,
    recoveryMethod: "NONE", recoveryRate: null
  },
  [CLIENT_CODES.G_SOLAR]: {
    clientName: "GSolar Power",
    // Real invoices (Aug 2026) show 0% WHT and Sindh Sales Tax at 15% (not
    // the usual 16%) — charged only on the service-charge line.
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0, gstRate: 0.15, gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY, gstWithholdingRate: 0,
    recoveryMethod: "NONE", recoveryRate: null
  },
  [CLIENT_CODES.VS_ALPHA]: {
    clientName: "VS Alpha",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0, gstRate: 0.15, gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY, gstWithholdingRate: 0,
    recoveryMethod: "NONE", recoveryRate: null
  },
  [CLIENT_CODES.NAGINA]: {
    clientName: "Nagina Electronics",
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07, gstRate: 0.16, gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY, gstWithholdingRate: 0.20,
    recoveryMethod: "NONE", recoveryRate: null
  },
  [CLIENT_CODES.UNITED_DETERGENT]: {
    clientName: "United Detergents",
    // Real invoices (Aug 2026, Unit I) show 0% WHT and explicitly ask the
    // client not to withhold GST — service charge is a flat 6% of the
    // reimbursement subtotal, GST 16% on that service charge only.
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0, gstRate: 0.16, gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY, gstWithholdingRate: 0,
    recoveryMethod: "NONE", recoveryRate: null
  },
  [CLIENT_CODES.CORTEVA_CROP]: {
    clientName: "Corteva Crop Pakistan (Private) Limited",
    // Mirrors CORTEVA's own profile — IFS invoices use the identical
    // reimbursement + service-charge + GST mechanism as every other Corteva
    // segment, just billed under this client's own NTN/address. This profile
    // is informational for IFS; the separate flat-fee Consultant invoice
    // (Consultancy fee + 4% tax + service admin charge + 16% GST on that
    // charge) computes its own tax lines directly and doesn't use this
    // profile's gstRate/whtRate at all — see cortevaCropAdapter.js.
    whtModel: WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: 0.07, gstRate: 0.16, gstScope: GST_SCOPES.SERVICE_CHARGES_ONLY, gstWithholdingRate: 0.20,
    recoveryMethod: "NONE", recoveryRate: null
  }
});

// {code, label} for every client, in one place — used by ClientPicker on
// every Management Finance page instead of each page building its own list.
export const CLIENT_PICKER_LIST = Object.values(CLIENT_CODES).map((code) => ({
  code, label: SUGGESTED_DEFAULTS[code]?.clientName || code
}));

export async function listProfiles(clientCode) {
  const q = clientCode
    ? query(collection(db, COLLECTION), where("clientCode", "==", clientCode))
    : collection(db, COLLECTION);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Returns the single ACTIVE profile for a client, or null if none has been
// activated yet (e.g. CBRE/CPBM until finance reviews a recovery rate).
export async function getActiveProfile(clientCode) {
  const pointerSnap = await getDoc(doc(db, ACTIVE_POINTERS, clientCode));
  if (!pointerSnap.exists()) return null;
  const data = pointerSnap.data();
  return { id: data.profileId, ...data };
}

// getActiveProfile() variant usable from inside a Firestore transaction, so an
// approval flow can read the active profile and write the snapshot atomically
// (no risk of the profile being reactivated mid-approval). Reads the pointer
// doc (a plain document reference) rather than running a query, since
// transaction.get() in the Web SDK cannot take a query.
export async function getActiveProfileInTransaction(transaction, clientCode) {
  const pointerSnap = await transaction.get(doc(db, ACTIVE_POINTERS, clientCode));
  if (!pointerSnap.exists()) return null;
  const data = pointerSnap.data();
  return { id: data.profileId, ...data };
}

export async function createDraftProfile(input) {
  const clientCode = String(input.clientCode || "").trim().toUpperCase();
  if (!clientCode) throw new Error("A client code is required.");
  if (!Object.values(WHT_MODELS).includes(input.whtModel)) throw new Error("A valid WHT model is required.");
  const profiles = await listProfiles(clientCode);
  const version = 1 + profiles.reduce((max, p) => Math.max(max, Number(p.version || 0)), 0);
  const profileRef = doc(collection(db, COLLECTION));
  const profile = {
    clientCode,
    clientName: input.clientName || clientCode,
    whtModel: input.whtModel,
    whtRate: Number(input.whtRate || 0),
    gstRate: Number(input.gstRate || 0),
    gstScope: input.gstScope || GST_SCOPES.SERVICE_CHARGES_ONLY,
    gstWithholdingRate: Number(input.gstWithholdingRate || 0),
    recoveryMethod: input.recoveryMethod || "NONE",
    recoveryRate: input.recoveryRate == null ? null : Number(input.recoveryRate),
    recoveryVarianceTolerance: Number(input.recoveryVarianceTolerance || 5),
    // Used by Stage 2's AR aging (arAgingService.js) to derive a due date from
    // the invoice date when no explicit due date is set on the invoice.
    paymentTermsDays: Number(input.paymentTermsDays || 30),
    roundingPolicy: "ROUND_HALF_UP",
    effectiveFrom: input.effectiveFrom || new Date().toISOString().slice(0, 10),
    effectiveTo: null,
    version,
    status: "DRAFT",
    createdBy: auth.currentUser?.uid || null,
    createdByEmail: auth.currentUser?.email || null,
    createdAt: serverTimestamp()
  };
  await runTransaction(db, async (transaction) => {
    transaction.set(profileRef, profile);
  });
  return { id: profileRef.id, ...profile };
}

// Activates a DRAFT profile, closing out the client's prior ACTIVE version
// (effectiveTo = today, status = SUPERSEDED) and repointing the active
// pointer doc, all in one transaction using only document references —
// finding the "prior active" doc via the pointer (not a query) is what makes
// this transaction-safe in the Web SDK.
export async function activateProfile(profileId) {
  const profileRef = doc(db, COLLECTION, profileId);
  await runTransaction(db, async (transaction) => {
    const profileSnap = await transaction.get(profileRef);
    if (!profileSnap.exists()) throw new Error("Tax profile not found.");
    const profile = profileSnap.data();
    if (profile.status === "ACTIVE") return;

    const activePointerRef = doc(db, ACTIVE_POINTERS, profile.clientCode);
    const activePointerSnap = await transaction.get(activePointerRef);
    const previousActiveId = activePointerSnap.exists() ? activePointerSnap.data().profileId : null;

    const today = new Date().toISOString().slice(0, 10);
    const effectiveFrom = profile.effectiveFrom || today;

    if (previousActiveId && previousActiveId !== profileId) {
      transaction.set(doc(db, COLLECTION, previousActiveId), { status: "SUPERSEDED", effectiveTo: today }, { merge: true });
    }

    transaction.update(profileRef, {
      status: "ACTIVE",
      effectiveFrom,
      activatedBy: auth.currentUser?.uid || null,
      activatedByEmail: auth.currentUser?.email || null,
      activatedAt: serverTimestamp()
    });

    transaction.set(activePointerRef, {
      ...profile,
      profileId,
      status: "ACTIVE",
      effectiveFrom,
      updatedAt: serverTimestamp()
    });
  });
}

// Turns off whichever profile is currently ACTIVE for a client — e.g. after
// activating the wrong one by mistake. Marks it INACTIVE (distinct from the
// automatic SUPERSEDED that activateProfile() applies when a newer version
// replaces an older one) and removes the active pointer, so approvals fall
// back to WHT/GST = 0 until a corrected profile is activated.
export async function deactivateProfile(clientCode) {
  const activePointerRef = doc(db, ACTIVE_POINTERS, clientCode);
  await runTransaction(db, async (transaction) => {
    const activePointerSnap = await transaction.get(activePointerRef);
    if (!activePointerSnap.exists()) return;
    const previousActiveId = activePointerSnap.data().profileId;
    transaction.set(doc(db, COLLECTION, previousActiveId), {
      status: "INACTIVE",
      effectiveTo: new Date().toISOString().slice(0, 10),
      deactivatedBy: auth.currentUser?.uid || null,
      deactivatedByEmail: auth.currentUser?.email || null,
      deactivatedAt: serverTimestamp()
    }, { merge: true });
    transaction.delete(activePointerRef);
  });
}

// Direct in-place correction of a profile's values — for fixing a mistake
// (wrong rate, wrong model, etc.) without spinning up a whole new version.
// Safe regardless of the profile's status: any invoice already approved
// against this profile has its own computed WHT/GST baked into its
// approved_invoice_snapshots doc already, so editing the profile afterwards
// never rewrites history — it only changes what future approvals will use.
// If this profile is the client's current ACTIVE one, the active pointer's
// mirrored copy is refreshed in the same transaction so the fix takes effect
// immediately, not just on the next activation.
export async function updateProfileFields(profileId, updates) {
  const profileRef = doc(db, COLLECTION, profileId);
  const fields = {
    ...(updates.clientName != null && { clientName: updates.clientName }),
    ...(updates.whtModel != null && { whtModel: updates.whtModel }),
    ...(updates.whtRate != null && { whtRate: Number(updates.whtRate) }),
    ...(updates.gstRate != null && { gstRate: Number(updates.gstRate) }),
    ...(updates.gstScope != null && { gstScope: updates.gstScope }),
    ...(updates.gstWithholdingRate != null && { gstWithholdingRate: Number(updates.gstWithholdingRate) }),
    ...(updates.recoveryMethod != null && { recoveryMethod: updates.recoveryMethod }),
    ...(updates.recoveryRate !== undefined && { recoveryRate: updates.recoveryRate === "" || updates.recoveryRate == null ? null : Number(updates.recoveryRate) }),
    ...(updates.recoveryVarianceTolerance != null && { recoveryVarianceTolerance: Number(updates.recoveryVarianceTolerance) }),
    ...(updates.paymentTermsDays != null && { paymentTermsDays: Number(updates.paymentTermsDays) }),
    ...(updates.effectiveFrom != null && { effectiveFrom: updates.effectiveFrom })
  };

  await runTransaction(db, async (transaction) => {
    // Both reads happen up front — Firestore requires every transaction.get()
    // to happen before any write, so the pointer is read unconditionally here
    // rather than after deciding (from a write-triggering status check).
    const profileSnap = await transaction.get(profileRef);
    if (!profileSnap.exists()) throw new Error("Tax profile not found.");
    const profile = profileSnap.data();

    const activePointerRef = doc(db, ACTIVE_POINTERS, profile.clientCode);
    const activePointerSnap = await transaction.get(activePointerRef);

    transaction.set(profileRef, {
      ...fields,
      revisedBy: auth.currentUser?.uid || null,
      revisedByEmail: auth.currentUser?.email || null,
      revisedAt: serverTimestamp()
    }, { merge: true });

    if (profile.status === "ACTIVE" && activePointerSnap.exists() && activePointerSnap.data().profileId === profileId) {
      transaction.set(activePointerRef, { ...fields, updatedAt: serverTimestamp() }, { merge: true });
    }
  });
}
